import { Dispatch, SetStateAction, useEffect } from "react";
import {
	WeeklyClass,
	DayCourseData,
	DayEventData,
	EventData,
	Class,
	CalendarItem,
} from "./types";
function determineDateFormat(inputDate: string) {
	if (inputDate.length === 19 && inputDate.charAt(10) === " ") {
		return "yyyy-MM-dd HH:mm:ss";
	} else if (inputDate.length === 8) {
		return "yyyyMMdd";
	}
}

export function getDate(inputDate: string) {
	let formattedDate = "";
	// Parse the input date string
	if (determineDateFormat(inputDate) === "yyyy-MM-dd HH:mm:ss") {
		formattedDate = inputDate.slice(0, -9) + "T00:00:00";
	} else if (determineDateFormat(inputDate) === "yyyyMMdd") {
		const year = inputDate.substring(0, 4);
		const month = inputDate.substring(4, 6);
		const day = inputDate.substring(6, 8);

		// Return the formatted date string
		formattedDate = `${year}-${month}-${day}T00:00:00`;
	}

	return formattedDate;
}

export function padNumber(num: number) {
	// Add leading zero if the number is less than 10
	return num < 10 ? `0${num}` : num;
}

export function getTimeSpan(startDate: string, endDate: string) {
	// Parse the input date strings
	const parsedStartDate = new Date(startDate.replace(" ", "T"));
	const parsedEndDate = new Date(endDate.replace(" ", "T"));

	// Format the time in HH:mm format
	const startTime = `${padNumber(parsedStartDate.getHours())}:${padNumber(
		parsedStartDate.getMinutes()
	)}`;
	const endTime = `${padNumber(parsedEndDate.getHours())}:${padNumber(
		parsedEndDate.getMinutes()
	)}`;

	// Formatted time span
	const formattedTimeSpan = `${startTime}-${endTime}`;

	return formattedTimeSpan;
}

export function areSameDay(date1: Date, date2: Date) {
	return (
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth() &&
		date1.getDate() === date2.getDate()
	);
}

export const months = [
	"Januar",
	"Februar",
	"März",
	"April",
	"Mai",
	"Juni",
	"Juli",
	"August",
	"September",
	"Oktober",
	"November",
	"Dezember",
];
export function formatDateWithWeekday(inputDate: string) {
	const date = new Date(inputDate);
	const day = date.getDate();
	const month = months[date.getMonth()];
	const year = date.getFullYear();

	return `${day}. ${month} ${year}`;
}

export function getMonthName(inputDate: Date) {
	const month = months[inputDate.getMonth()];
	return month;
}

export const groupEventsByMonth = (events: EventData[]) => {
	const groupedEvents: Record<string, EventData[]> = {};
	events.forEach((event) => {
		const monthKey: string = new Date(event.acf.start_date_time).toLocaleString(
			"de-DE",
			{
				month: "long",
			}
		);
		if (!groupedEvents[monthKey]) {
			groupedEvents[monthKey] = [];
		}
		groupedEvents[monthKey].push(event);
	});

	// Sort each group by acf.start_date_time
	Object.values(groupedEvents).forEach((group) => {
		group.sort((a, b) => {
			const dateA = new Date(a.acf.start_date_time).getTime();
			const dateB = new Date(b.acf.start_date_time).getTime();
			return dateA - dateB;
		});
	});

	return groupedEvents;
};

export function findClassOnThisDay(course: DayCourseData, dayDate: Date) {
	if (course.acf.courses && course.acf.courses.length > 0) {
		return course.acf.courses.some((classItem: WeeklyClass) => {
			if (classItem.start_date_time) {
				const classStartDate = new Date(getDate(classItem.start_date_time));

				const sameWeekDay = classStartDate.getDay() === dayDate.getDay();
				if (sameWeekDay) {
					const alreadyStarted = classStartDate <= dayDate;

					const notYetFinished = classItem.last_day
						? new Date(getDate(classItem.last_day)) >= dayDate
						: true;

					if (alreadyStarted && notYetFinished) {
						if (course.acf.cancelled_on) {
							const formattedCancelledOn = course.acf.cancelled_on.map((date) =>
								getDate(date.cancelled_date)
							);

							return !formattedCancelledOn.some((cancelledDate) => {
								return areSameDay(new Date(cancelledDate), dayDate);
							});
						}
						course.day_start_date_time = new Date(classItem.start_date_time);
						course.day_end_date_time = new Date(classItem.end_date_time);

						return true;
					}
				}
			}

			return false;
		});
	}

	return false;
}

export function findWeeklyClassForDate(
	courses: WeeklyClass[],
	currentDate: Date
) {
	const dayDate = new Date(currentDate);

	for (const classItem of courses) {
		const classDate = new Date(getDate(classItem.start_date_time));
		if (classDate.getDay() === dayDate.getDay()) {
			return classItem;
		}
	}

	return null;
}

export function findCustomClassForDate(
	courses: Class[] | null,
	currentDate: Date
) {
	if (courses) {
		const dayDate = new Date(currentDate);

		return courses.find((course) => {
			const classDate = new Date(getDate(course.start_date_time));
			return areSameDay(dayDate, classDate);
		});
	}

	return null;
}

export function compareStartTimes(
	a: DayCourseData | DayEventData,
	b: DayCourseData | DayEventData
) {
	if (a.day_start_date_time === undefined) {
		return 1;
	}
	if (b.day_start_date_time === undefined) {
		return -1;
	}

	const timeA =
		a.day_start_date_time.getHours() * 60 + a.day_start_date_time.getMinutes();
	const timeB =
		b.day_start_date_time.getHours() * 60 + b.day_start_date_time.getMinutes();

	return timeA - timeB;
}

export function getFormattedTimeSpanAndDate(
	event: CalendarItem,
	dayDate: Date
) {
	let formattedDate: string = "";
	let timeSpan: string = event.title.rendered;

	if (
		!event.event &&
		"weekly_or_custom" in event.acf &&
		event.acf.weekly_or_custom === "custom" &&
		"custom_courses" in event.acf &&
		event.acf.custom_courses &&
		event.acf.custom_courses.length > 0
	) {
		const courseCustomClass = findCustomClassForDate(
			event.acf.custom_courses,
			dayDate as Date
		);

		if (courseCustomClass) {
			formattedDate = formatDateWithWeekday(dayDate?.toDateString() as string);
			timeSpan = getTimeSpan(
				courseCustomClass.start_date_time as string,
				courseCustomClass.end_date_time as string
			);
		}
	} else if (
		!event.event &&
		"courses" in event.acf &&
		event.acf.courses &&
		event.acf.courses.length > 0
	) {
		const courseWeeklyClass = findWeeklyClassForDate(
			event.acf.courses,
			dayDate as Date
		);
		if (courseWeeklyClass) {
			formattedDate = formatDateWithWeekday(dayDate?.toDateString() as string);
			timeSpan = getTimeSpan(
				courseWeeklyClass.start_date_time as string,
				courseWeeklyClass.end_date_time as string
			);
		}
	} else if (
		event.event &&
		"start_date_time" in event.acf &&
		"end_date_time" in event.acf
	) {
		const startDate = new Date(
			event.acf.start_date_time.replace(" ", "T") as string
		);
		const endDate = new Date(event.acf.end_date_time.replace(" ", "T") as string);

		if (
			startDate.getDate() === endDate.getDate() &&
			startDate.getMonth() === endDate.getMonth() &&
			startDate.getFullYear() === endDate.getFullYear()
		) {
			const startDay = startDate.getDate();

			formattedDate = `${startDay}. ${
				months[startDate.getMonth()]
			} ${startDate.getFullYear()}`;
		} else if (
			startDate.getMonth() === endDate.getMonth() &&
			startDate.getFullYear() === endDate.getFullYear()
		) {
			const startDay = startDate.getDate();
			const endDay = endDate.getDate();

			formattedDate = `${startDay}.–${endDay}. ${
				months[startDate.getMonth()]
			} ${startDate.getFullYear()}`;
		} else if (
			startDate.getFullYear() === endDate.getFullYear() &&
			startDate.getMonth() !== endDate.getMonth()
		) {
			const startDay = startDate.getDate();
			const endDay = endDate.getDate();
			const startMonth = months[startDate.getMonth()];
			const endMonth = months[endDate.getMonth()];

			formattedDate = `${startDay}. ${startMonth}–${endDay}. ${endMonth} ${startDate.getFullYear()}`;
		} else {
			const startDay = startDate.getDate();
			const endDay = endDate.getDate();
			const startMonth = months[startDate.getMonth()];
			const endMonth = months[endDate.getMonth()];
			const startYear = startDate.getFullYear();
			const endYear = endDate.getFullYear();

			formattedDate = `${startDay}. ${startMonth} ${startYear}–${endDay}. ${endMonth} ${endYear}`;
		}

		timeSpan = getTimeSpan(
			event.acf.start_date_time as string,
			event.acf.end_date_time as string
		);
	}

	const formattedDateAndTime = {
		timeSpan,
		formattedDate,
	};

	return formattedDateAndTime;
}

type SetStateFunction<T> = Dispatch<SetStateAction<T>>;

export const useApiData = (
	endpoint: string,
	setStateFunction: SetStateFunction<any[]>
) => {
	const fetchData = async (page: number) => {
		try {
			const response = await fetch(`${endpoint}?per_page=10&page=${page}`);

			if (!response.ok) {
				throw new Error(`Error fetching data: ${response.statusText}`);
			}

			const totalPages = parseInt(
				response.headers.get("X-WP-TotalPages") || "1",
				10
			);
			const data = await response.json();

			return { data, totalPages };
		} catch (error) {
			return { data: [], totalPages: 1 };
		}
	};
	useEffect(() => {
		let page = 1;

		let allNodes: any[] = [];

		const fetchAllData = async () => {
			while (true) {
				const { data, totalPages } = await fetchData(page);

				if (!data || data.length === 0) {
					break;
				}

				allNodes = [...allNodes, ...data];
				page++;
				if (page > totalPages) {
					break;
				}
			}

			setStateFunction(allNodes);
		};

		fetchAllData();
	}, [endpoint, setStateFunction]);
};

export function extractTextUntilFirstBr(
	html: string,
	maxWords = 10,
	ellipsis = "..."
) {
	// Create a temporary div element to parse the HTML
	const tempDiv = document.createElement("div");
	tempDiv.innerHTML = html;

	// Extract text until the first <br> tag
	const textUntilFirstBr = tempDiv.innerText.split("\n")[0].trim();

	// Extract the first 'maxWords' words from the text
	const words = textUntilFirstBr.split(" ");
	const extractedText = words.slice(0, maxWords).join(" ");

	// Add '...' if the text exceeds 'maxWords'
	const displayText =
		words.length > maxWords ? `${extractedText}${ellipsis}` : extractedText;

	return displayText;
}
